// src/pages/FullViewerMobile.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import HeadingCp from '../components/HeadingCp';
import { formatTimeTo12HourUtil } from '../utils/formatTimeTo12HourUtil';
import LegendCP from '../components/LegendCP';

const HOURS = Array.from({ length: 15 }, (_, i) => i + 6); // 6am to 8pm

const FullViewerMobile = () => {
  const [events, setEvents] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const apiUrl = process.env.REACT_APP_API_URL;
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState('All');

  //adding a heper for modal
  const [selected, setSelected] = useState(null);
  const hasNote = (ev) => getNote(ev).length > 0;
  const openNotes = (ev) => setSelected(ev);
  // const closeNotes = () => setSelected(null);

  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const noteRef = useRef(null);

  const getNote = (ev) => (ev?.tech_notes || '').trim();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // {query, service, inspection, wip, merged?}
  const cancelRef = useRef(null);
  const debounceRef = useRef(null);

  // helpers
const getInternalNote = (ev) => (ev?.notes || ev?.user_comment || '').trim();
const hasInternalNote = (ev) => getInternalNote(ev).length > 0;

// state for INTERNAL NOTE (new)
const [intExpanded, setIntExpanded] = useState(false);
const [intCanExpand, setIntCanExpand] = useState(false);
const intRef = useRef(null);

const isSearching = !!searchResults?.merged;

  // when a new record opens, reset & measure both sections
  useEffect(() => {
    setExpanded(false);
    setCanExpand(false);
    setIntExpanded(false);
    setIntCanExpand(false);
    const t = setTimeout(() => {
      if (noteRef.current) {
        const el = noteRef.current;
        setCanExpand(el.scrollHeight > el.clientHeight + 2);
      }
      if (intRef.current) {
        const el = intRef.current;
        setIntCanExpand(el.scrollHeight > el.clientHeight + 2);
      }
    }, 0);
    return () => clearTimeout(t);
  }, [selected]);

// whenever you open a new record, reset + measure
  // useEffect(() => {
  //   setExpanded(false);
  //   setCanExpand(false);
  //   // wait for paint, then measure
  //   const t = setTimeout(() => {
  //     if (noteRef.current) {
  //       const el = noteRef.current;
  //       setCanExpand(el.scrollHeight > el.clientHeight + 2);
  //     }
  //   }, 0);
  //   return () => clearTimeout(t);
  // }, [selected]);

  useEffect(() => {
    fetchTechnicians();
    fetchEventsForDay(currentDate);
    // eslint-disable-next-line
  }, [currentDate]);

  const closeNotes = () => {
    setExpanded(false);
    setIntExpanded(false);
    setSelected(null);
  };  

  const fetchTechnicians = async () => {
    try {
      const res = await axios.get(`${apiUrl}/contracts_crud.php?employee_list=true`);
      const list = Array.isArray(res.data) ? res.data : [];
      setTechnicians(list);
      // Extract unique departments
      const uniqueDepts = Array.from(
        new Set(list.map(t => t.department).filter(Boolean))
      ).sort();
      setDepartments(uniqueDepts);
    } catch (err) {
      // silent fail on mobile
    }
  };

  // Parse 'YYYY-MM-DD' as LOCAL date (no UTC shift)
  const parseLocalDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return null;
    const [y, m, d] = dateString.slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 0, 0, 0);
  };

  // Normalize API payload into one shape
  const normalize = (data, type) => {
    return (Array.isArray(data) ? data : []).map(ev => ({
      type,
      id: ev.id || ev.schedule_id || ev.inspection_id,
      tech_id: ev.tech_id || ev.lead_tech_id || ev.TECH,
      add_tech: ev.add_tech || [],
      job_site: ev.job_site_address || ev.job_site || ev.ADDRESS || ev.description || '(No Address)',
      customer: ev.customer_name || ev.CUSTOMER || '',
      job_number: ev.job_number || '',
      date_from: ev.scheduled_due_date || ev.SCHEDULE_DATE || ev.schedule_from,
      date_to: ev.SCHEDULE_DATE || ev.schedule_date_to || ev.schedule_to,
      start_time: ev.schedule_time_in || ev.TIME || ev.start_time,
      end_time: ev.schedule_time_out || ev.end_time || ev.end_time,
      TECH: ev.TECH,
      STATUS: ev.STATUS,
      TROUBLE: ev.TROUBLE || '',
      tech_notes: ev.tech_notes || ev.TECHNOTES || '',
      // notes fields (useful for future badges)
      notes: ev.notes
          || ev.user_comment        // wip
          || ev.NOTES               // service
          || ev.inspection_notes    // inspections
          // || ev.contract_notes      // include mc.notes in your SELECT as contract_notes (you already do)
          || '',
      // description: ev.description || '',
      status_raw: ev.status || ev.inspection_status || ev.STATUS || '',
    }));
  };

  // Fetch day events across modules, filter to currentDate range
  const fetchEventsForDay = async (date) => {
    // const iso = date.toISOString().split('T')[0];
    const iso =
      date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');

    try {
      const [insRes, svcRes, wipRes, opeRes] = await Promise.all([
        axios.get(`${apiUrl}/contracts_crud.php`, { params: { getInspections: 'all', startDate: iso, endDate: iso } }),
        axios.get(`${apiUrl}/service_crud.php?getListByDate=1&date=${iso}`),
        axios.get(`${apiUrl}/wip_crud.php`, { params: { getWipScheduleList: 'all', startDate: iso, endDate: iso } }),
        axios.get(`${apiUrl}/operation_crud.php`, { params: { getOpScheduleList: 1, month:'all', startDate: iso, endDate: iso } }),
      ]);
      const merged = [
        ...normalize(insRes.data?.[0], 'inspection'),
        ...normalize(svcRes.data, 'service'),
        ...normalize(wipRes.data?.[0], 'wip'),
        ...normalize(opeRes.data?.[0], 'operation'),
      ];

      const cd = new Date(date); cd.setHours(0, 0, 0, 0);

      const onlyEventsOnDate = merged.filter(ev => {
        const from = parseLocalDate(ev.date_from);
        const to = parseLocalDate(ev.date_to || ev.date_from);
        if (!from || !to) return false;
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return cd >= from && cd <= to;
      });

      setEvents(onlyEventsOnDate);
    } catch (err) {
      // silent fail
    }
  };

  const filteredTechs = selectedDept === 'All'
                      ? technicians
                      : technicians.filter(tech => tech.department === selectedDept);

  const handlePreviousDay = () => {
    const prev = new Date(currentDate);
    prev.setDate(prev.getDate() - 1);
    setCurrentDate(prev);
  };

  const handleNextDay = () => {
    const next = new Date(currentDate);
    next.setDate(next.getDate() + 1);
    setCurrentDate(next);
  };

  const typeBadgeClass = (type) =>
    type === 'inspection'
      ? 'bg-green-100 text-green-700 ring-green-600/20'
      : type === 'service'
      ? 'bg-blue-100 text-blue-700 ring-blue-600/20'
      : type === 'wip'
      ? 'bg-purple-100 text-purple-700 ring-purple-600/20'
      : 'bg-orange-100 text-orange-700 ring-orange-600/20';

  const isHelper = (ev, techId) =>
    Array.isArray(ev.add_tech) && ev.add_tech.some(t => String(t.tech_id) === String(techId));

  const debounce = (fn, delay = 350) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fn, delay);
  };

    // Normalize backend payload into your event shape
  const normalizeSearchPayload = (payload) => {
    const svc = Array.isArray(payload?.service) ? payload.service : [];
    const ins = Array.isArray(payload?.inspection) ? payload.inspection : [];
    const wip = Array.isArray(payload?.wip) ? payload.wip : [];

    const n = [];

    svc.forEach(ev => n.push({
      type:'service',
      id: ev.id,
      tech_id: ev.tech_id || null,
      add_tech: ev.add_tech || [],
      job_site: ev.ADDRESS || '(No Address)',
      customer: ev.CUSTOMER || '',
      job_number: ev.SERVICE_TICKET || '',
      date_from: ev.SCHEDULE_DATE,
      date_to: ev.SCHEDULE_DATE,
      start_time: ev.TIME,
      end_time: ev.end_time,
      tech_notes: ev.TECHNOTES || '',
      notes: ev.NOTES || '',
      STATUS: ev.STATUS || '',
      TROUBLE: ev.TROUBLE || ''
    }));

    ins.forEach(ev => n.push({
      type:'inspection',
      id: ev.inspection_id,
      tech_id: ev.lead_tech_id || null,
      add_tech: [],
      job_site: ev.job_site_address || `${ev.job_site_city ?? ''} ${ev.job_site_state ?? ''}`.trim(),
      customer: ev.customer_name || '',
      job_number: '',
      date_from: ev.scheduled_due_date,
      date_to: ev.schedule_date_to || ev.scheduled_due_date,
      start_time: ev.schedule_time_in,
      end_time: ev.schedule_time_out,
      tech_notes: ev.tech_notes || '',
      notes: '',
      STATUS: ev.status || ''
    }));

    wip.forEach(ev => n.push({
      type:'wip',
      id: ev.schedule_id,
      tech_id: ev.tech_id || null,
      add_tech: [],
      job_site: ev.job_site || '',
      customer: ev.customer_name || '',
      job_number: ev.job_number || '',
      date_from: ev.date_from,
      date_to: ev.date_to,
      start_time: ev.start_time,
      end_time: ev.end_time,
      tech_notes: ev.tech_notes || '',
      notes: ev.notes || '',
      STATUS: ev.status || ''
    }));

    return n;
  };

  const runSearch = (term) => {
    const q = (term || '').trim();
    if (q.length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }

    setSearching(true);

    // cancel previous request
    if (cancelRef.current) cancelRef.current.cancel('New search');
    cancelRef.current = axios.CancelToken.source();

    axios.post(
      `${apiUrl}/search_crud.php`,   // or contracts_crud.php?search=1
      { q },                              // JSON body
      {
        cancelToken: cancelRef.current.token,
        headers: { 'Content-Type': 'application/json' },
      }
    )
    .then(res => {
      const data = res.data || {};
      setSearchResults({
        query: q,
        merged: normalizeSearchPayload(data),
        raw: data,
      });
    })

    .catch(err => {
      if (!axios.isCancel(err)) {
        setSearchResults({ query: q, merged: [], raw: { service:[], inspection:[], wip:[] } });
      }
    })
    .finally(() => setSearching(false));
  };  
  

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Sticky header with date controls */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md ring-1 ring-gray-200 shadow-sm">
        <div className="mx-auto max-w-md px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-base font-semibold text-gray-900">Full Scheduler</h1>
            <div className="flex items-center gap-2">
              <button
                className="rounded-xl border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                onClick={handlePreviousDay}
              >
                Prev
              </button>
              <button
                className="rounded-xl border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                onClick={handleNextDay}
              >
                Next
              </button>
            </div>
          </div>
          <div className="mt-1 text-sm font-medium text-gray-700">
            {currentDate.toLocaleDateString('en-US', {
              weekday: 'long', year: 'numeric', month: 'short', day: 'numeric'
            })}
          </div>

          {/* Legend & Department */}
          <div className="mt-3 flex items-center justify-between">
            <div className="flex gap-2">
              <span className="inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">Inspections</span>
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">Service</span>
              <span className="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20">WIP</span>
              <span className="inline-flex items-center rounded-full bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-600/20">Operation</span>
            </div>
          </div>

          <div className="mt-3">
            <select
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              value={selectedDept}
              onChange={e => setSelectedDept(e.target.value)}
            >
              <option value="All">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
          <div className="mt-3">
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => {
                const v = e.target.value;
                setSearchTerm(v);
                debounce(() => runSearch(v), 350);
              }}
              placeholder="Search address, customer, or job #"
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
            {searchTerm && (
              <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                <span>{searching ? 'Searching…' : 'Tap a result to view notes'}</span>
                <button
                  type="button"
                  onClick={() => { setSearchTerm(''); setSearchResults(null); }}
                  className="text-indigo-600 hover:underline"
                >
                  Clear
                </button>
              </div>
            )}
          </div>          
        </div>        
      </div>

      {/* Tech cards list */}
      <div className="mx-auto max-w-md px-4 py-4 space-y-4">
        {isSearching ? (
          searchResults.merged.length ? (
            searchResults.merged.map(ev => {
              const badgeCls = typeBadgeClass(ev.type);
              return (
                <div
                  key={`${ev.type}-${ev.id}`}
                  className="rounded-2xl bg-white/95 backdrop-blur shadow ring-1 ring-gray-200"
                >
                  <div
                    className="p-3 space-y-1 hover:bg-gray-50 transition-colors active:bg-gray-100 cursor-pointer"
                    onClick={() => openNotes(ev)}
                    role="button"
                    aria-label="View tech notes"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="truncate font-semibold text-gray-900">
                        {ev.job_site || '(No Address)'}
                      </div>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${badgeCls}`}
                      >
                        {ev.type}
                      </span>
                    </div>

                    {ev.customer && (
                      <div className="truncate text-xs text-gray-600">
                        {ev.customer}
                      </div>
                    )}

                    {(ev.start_time || ev.end_time) && (
                      <div className="text-xs text-gray-700">
                        {formatTimeTo12HourUtil(ev.start_time)} –{' '}
                        {formatTimeTo12HourUtil(ev.end_time)}
                      </div>
                    )}

                    {ev.job_number && (
                      <div className="text-[11px] text-gray-500">
                        #{ev.job_number}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center text-gray-400">No matches.</div>
          )
        ) : (
          <>
            {filteredTechs.map(tech => {
              // Events assigned to this tech
              const eventsForTech = events.filter(ev => {
                const lead = String(ev.tech_id) === String(tech.id);
                const helper = isHelper(ev, tech.id);
                return lead || helper;
              });

              if (!eventsForTech.length) return null;

              return (
                <div
                  key={tech.id}
                  className="rounded-2xl bg-white/95 backdrop-blur shadow ring-1 ring-gray-200"
                >
                  {/* ── Tech Header ───────────────────────────── */}
                  <div className="flex items-center justify-between rounded-t-2xl bg-gray-50 px-4 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-gray-900">
                        {tech.first_name} {tech.last_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {tech.department || '—'}
                      </div>
                    </div>
                  </div>

                  {/* ── Events List ───────────────────────────── */}
                  <div className="divide-y">
                    {eventsForTech.map(ev => {
                      const badgeCls = typeBadgeClass(ev.type);
                      const isHelp = isHelper(ev, tech.id);

                      return (
                        <div
                          key={`${ev.id}-${ev.type}`}
                          className="cursor-pointer space-y-1 p-3 transition-colors hover:bg-gray-50 active:bg-gray-100"
                          onClick={() => openNotes(ev)}
                          role="button"
                          aria-label="View tech notes"
                        >
                          {/* Job site + Type badges */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="truncate font-semibold text-gray-900">
                              {ev.job_site}
                            </div>
                            <div className="flex items-center gap-1">
                              {hasNote(ev) && (
                                <span className="shrink-0 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
                                  Notes
                                </span>
                              )}
                              <span
                                className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${badgeCls}`}
                              >
                                {ev.type}
                              </span>
                            </div>
                          </div>

                          {/* Customer */}
                          <div className="truncate text-xs text-gray-600">
                            {ev.customer}
                          </div>

                          {/* Time + Helper */}
                          <div className="flex items-center justify-between text-xs text-gray-700">
                            <span>
                              {formatTimeTo12HourUtil(ev.start_time)} –{' '}
                              {formatTimeTo12HourUtil(ev.end_time)}
                            </span>
                            {isHelp && (
                              <span className="font-semibold text-purple-700">
                                Helper
                              </span>
                            )}
                          </div>

                          {/* Trouble text */}
                          {ev.TROUBLE && (
                            <div className="text-xs text-gray-500 line-clamp-2">
                              <span className="font-medium text-gray-600">
                                Trouble:
                              </span>{' '}
                              {ev.TROUBLE}
                            </div>
                          )}

                          {/* Status badge */}
                          {ev.STATUS && (
                            <div className="inline-block w-max rounded-md bg-green-300 p-1 text-[11px] text-gray-800">
                              <span className="capitalize">
                                {String(ev.STATUS).toLowerCase()}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Bottom sheet for tech notes (render once) */}
      {selected && (
        <>
          {/* backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/30"
            onClick={closeNotes}
            aria-hidden="true"
          />
          {/* sheet */}
          <div
            className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl bg-white shadow-2xl ring-1 ring-gray-200"
            role="dialog"
            aria-modal="true"
          >
            <div className="mx-auto max-w-md p-4">
              {/* handle */}
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-300" />
              <div className="flex items-start justify-between">
                <h2 className="text-base font-semibold text-gray-900">Details</h2>
                <button
                  className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
                  onClick={closeNotes}
                >
                  Close
                </button>
              </div>

              {/* context chips */}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                <span className="inline-flex items-center px-2 py-0.5 font-medium text-gray-700 ring-1">
                  {selected?.job_site || '—'}
                </span>
                {selected?.customer && (
                  <span className="inline-flex items-center px-2 py-0.5 font-medium text-gray-700 ring-1">
                    {selected.customer}
                  </span>
                )}
                {(selected?.start_time || selected?.end_time) && (
                  <span className="inline-flex items-center px-2 py-0.5 font-medium text-gray-700 ring-1">
                    {formatTimeTo12HourUtil(selected.start_time)} – {formatTimeTo12HourUtil(selected.end_time)}
                  </span>
                )}
              </div>

              {/* NOTES (inline style for reliable height change) */}
              <div className="mt-4 relative">
                <div  className="rounded-lg bg-gray-50 p-3 text-sm text-gray-800 overflow-hidden transition-all duration-200"
                 >Tech Notes</div>
                <div
                  ref={noteRef}
                  className="rounded-lg bg-gray-100 p-3 text-sm text-gray-800 whitespace-pre-wrap break-words overflow-hidden transition-all duration-200"
                  style={{ maxHeight: expanded ? '60vh' : '4rem', overflowY: expanded ? 'auto' : 'hidden' }}
                >
                  {getNote(selected) || 'No tech notes for this record.'}
                </div>

                {/* subtle fade when collapsed */}
                {!expanded && canExpand && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 rounded-b-lg bg-gradient-to-t from-gray-50 to-transparent" />
                )}

                {/* toggle button only if text actually overflows */}
                {canExpand && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => setExpanded(v => !v)}
                      className="text-indigo-600 text-sm font-medium hover:underline"
                    >
                      {expanded ? 'Read less' : 'Read more'}
                    </button>
                  </div>
                )}
              </div>

              {/* ───────────────────────────── INTERNAL NOTE ───────────────────────────── */}
              {hasInternalNote(selected) && (
                <div className="mt-6 ">
                  <h3 className="text-sm font-semibold text-gray-900">Internal Note</h3>

                  <div className="mt-2 relative ">
                    <div
                      ref={intRef}
                      className="rounded-lg bg-blue-50 p-3 text-sm text-gray-800 whitespace-pre-wrap break-words overflow-hidden transition-all duration-200"
                      style={{
                        maxHeight: intExpanded ? '60vh' : '4rem',
                        overflowY: intExpanded ? 'auto' : 'hidden',
                      }}
                    >
                      {getInternalNote(selected)}
                    </div>

                    {/* fade when collapsed */}
                    {!intExpanded && intCanExpand && (
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 rounded-b-lg bg-gradient-to-t from-gray-50 to-transparent" />
                    )}

                    {/* toggle */}
                    {intCanExpand && (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => setIntExpanded((v) => !v)}
                          className="text-indigo-600 text-sm font-medium hover:underline"
                        >
                          {intExpanded ? 'Read less' : 'Read more'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}              

              {/* optional extras */}
              {selected?.description && (
                <div className="mt-3 text-xs text-red-600">
                  <span className="font-medium text-red-700">Description: </span>
                  <span className="whitespace-pre-wrap break-words">{selected.description}</span>
                </div>
              )}

              {/* optional extras */}
              {selected?.TROUBLE && (
                <div className="mt-3 text-xs text-red-600">
                  <span className="font-medium text-red-700">Trouble: </span>
                  <span className="whitespace-pre-wrap break-words">{selected.TROUBLE}</span>
                </div>
              )}

              {selected?.STATUS && (
                <div className="mt-2 text-[11px] inline-block rounded-md bg-green-100 px-2 py-0.5 font-medium text-green-800 ring-1 ring-green-600/20">
                  {String(selected.STATUS).toLowerCase()}
                </div>
              )}              

              <div className="mt-4 flex justify-end">
                <button
                  className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                  onClick={closeNotes}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FullViewerMobile;
